import {
  LOG_PREFIX,
  buildDashboardReport,
  buildRunRequest,
  columnResponseToColumnDefinition,
  createApiError,
  getDisplayDataType,
  pollUntilReady,
  reportsV3BaseQuery,
} from "./reportsV3ServiceHelpers";
import { createApi } from "@reduxjs/toolkit/query/react";
import * as logger from "common/logger";
import { getActiveLayout } from "components/reportsV3/utils/localLayout";
import {
  DASHBOARD_REPORT_ID,
  DASHBOARD_REPORT_STUB,
  INITIAL_METRICS,
  mergeSavedLayout,
} from "components/reportsV3/utils/submissionStatusSpec";
import { endpoints } from "config";
import { authenticatedFetch } from "utils";

const ID_ERROR = { error: { status: 400, data: "Report ID is required" } };

// Maps a run's column descriptors to column definitions, keeping the raw
// descriptor as `original` for use as a child report's expectedColumns.
export const columnDescriptorsToColumnDefinitions = (columns) =>
  columns
    .map((col) => {
      const type = col.type?.type ?? col.type;
      const subType = col.type?.subType ?? col.subType;
      return {
        original: col,
        internalName: col.internalName,
        displayName: col.displayName,
        type,
        subType,
        dataType: getDisplayDataType(type, subType),
      };
    })
    .filter((col) => col.type !== "DISPLAY_CONTROL");

export const reportsV3Api = createApi({
  reducerPath: "reportsV3Api",
  baseQuery: reportsV3BaseQuery,
  tagTypes: ["SavedReports"],
  endpoints: (builder) => ({
    getTables: builder.query({
      query: () => ({
        path: "tables",
        parseAs: "json",
        operation: "Failed to fetch tables",
      }),
    }),
    getColumnsForTables: builder.query({
      queryFn: async (tableNames) => {
        if (!tableNames || tableNames.length === 0) {
          return { data: [] };
        }
        const results = await Promise.all(
          tableNames.map(async (tableName) => {
            const result = await reportsV3BaseQuery({
              path: `tables/${tableName}`,
              operation: `Failed to fetch columns for table ${tableName}`,
            });
            if (result.error) return { error: result.error };
            return {
              data: result.data.columns.map((col) => ({
                ...columnResponseToColumnDefinition(col),
                sourceTable: tableName,
              })),
            };
          })
        );
        const firstError = results.find((r) => r.error);
        if (firstError) return { error: firstError.error };
        return { data: results.flatMap((r) => r.data) };
      },
    }),
    getEnumValuesForTables: builder.query({
      queryFn: async (tables) => {
        const nonEmpty = (tables ?? []).filter(
          (t) => t.columnNames && t.columnNames.length > 0
        );
        if (nonEmpty.length === 0) return { data: [] };
        const results = await Promise.all(
          nonEmpty.map(async ({ tableName, columnNames }) => {
            const columnsParam = columnNames.join(",");
            const result = await reportsV3BaseQuery({
              path: `tables/${tableName}/columns/${columnsParam}`,
              operation: `Failed to fetch enum values for ${tableName} columns [${columnNames.join(
                ", "
              )}]`,
            });
            if (result.error) return { error: result.error };
            return { data: result.data };
          })
        );
        const firstError = results.find((r) => r.error);
        if (firstError) return { error: firstError.error };
        return { data: results.flatMap((r) => r.data) };
      },
    }),
    getSavedReports: builder.query({
      queryFn: async () => {
        const result = await reportsV3BaseQuery({
          path: "reports",
          operation: "Failed to fetch saved reports",
        });
        if (result.error) return result;
        return { data: [DASHBOARD_REPORT_STUB, ...(result.data ?? [])] };
      },
      providesTags: ["SavedReports"],
    }),
    getReport: builder.query({
      queryFn: async (reportId) => {
        if (!reportId) {
          return ID_ERROR;
        }
        if (reportId === DASHBOARD_REPORT_ID) {
          try {
            // Review tab: overlay the user's active saved layout (if any) on
            // the config-driven default so the saved view is what executes.
            const { report: base, knownColumns } = await buildDashboardReport();
            return {
              data: mergeSavedLayout(
                base,
                getActiveLayout(reportId),
                knownColumns
              ),
            };
          } catch (error) {
            return createApiError("Failed to build dashboard report", error);
          }
        }
        return reportsV3BaseQuery({
          path: `reports/${reportId}`,
          operation: `Failed to fetch report ${reportId}`,
        });
      },
      providesTags: (result, error, reportId) => [
        {
          type: "SavedReports",
          id: reportId || "unknown",
        },
      ],
    }),
    createReport: builder.mutation({
      query: (reportData) => ({
        path: "reports",
        method: "POST",
        body: reportData,
        expectOk: true,
        operation: "Failed to create report",
      }),
      transformResponse: (data) => ({
        status: "SAVED",
        id: data.internalName,
      }),
      invalidatesTags: ["SavedReports"],
    }),
    updateReport: builder.mutation({
      query: ({ reportId, reportData }) => ({
        path: `reports/${reportId}`,
        method: "PUT",
        body: reportData,
        expectOk: true,
        operation: `Failed to update report ${reportId}`,
        logLevel: "warn",
      }),
      transformResponse: (data, meta, arg) => ({
        status: "SAVED",
        id: data.internalName || arg.reportId,
      }),
      invalidatesTags: (result, error, { reportId }) => [
        "SavedReports",
        {
          type: "SavedReports",
          id: reportId,
        },
      ],
    }),
    deleteReport: builder.mutation({
      query: (reportId) => ({
        path: `reports/${reportId}`,
        method: "DELETE",
        parseAs: "none",
        expectOk: true,
        operation: `Failed to delete report ${reportId}`,
        logLevel: "warn",
      }),
      transformResponse: () => ({
        status: "DELETED",
      }),
      invalidatesTags: ["SavedReports"],
    }),
    getReportMetadata: builder.query({
      queryFn: async (reportId) => {
        if (!reportId) {
          return ID_ERROR;
        }
        if (reportId === DASHBOARD_REPORT_ID) {
          return {
            data: { allUsers: true, readOnly: true, canWriteMetadata: false },
          };
        }
        const result = await reportsV3BaseQuery({
          path: `reports/${reportId}/metadata`,
          expectOk: true,
          operation: `Failed to fetch metadata for report ${reportId}`,
          logLevel: "warn",
        });
        if (result.error) {
          return result;
        }
        return {
          data: {
            allUsers: Boolean(result.data.allUsers),
            readOnly: Boolean(result.data.readOnly),
            canWriteMetadata: result.data.canWriteMetadata !== false,
          },
        };
      },
      providesTags: (result, error, reportId) => [
        {
          type: "SavedReports",
          id: `${reportId || "unknown"}-metadata`,
        },
      ],
    }),
    updateReportMetadata: builder.mutation({
      query: ({ reportId, metadata }) => ({
        path: `reports/${reportId}/metadata`,
        method: "PUT",
        body: metadata,
        expectOk: true,
        operation: `Failed to update metadata for report ${reportId}`,
        logLevel: "warn",
      }),
      transformResponse: (data) => ({
        allUsers: Boolean(data.allUsers),
        readOnly: Boolean(data.readOnly),
      }),
      invalidatesTags: (result, error, { reportId }) => [
        "SavedReports",
        {
          type: "SavedReports",
          id: reportId,
        },
        {
          type: "SavedReports",
          id: `${reportId}-metadata`,
        },
      ],
    }),
    executeReport: builder.mutation({
      queryFn: async ({
        reportId,
        outputType = "JSON",
        limitRows,
        skipRows,
        asAt,
      }) => {
        if (!reportId) {
          return ID_ERROR;
        }
        return pollUntilReady(
          () =>
            reportsV3BaseQuery({
              path: `reports/${reportId}/run`,
              method: "POST",
              body: buildRunRequest({ outputType, limitRows, skipRows, asAt }),
              parseAs: "json",
              operation: `Failed to execute report ${reportId}`,
            }),
          `execute report ${reportId}`,
          outputType
        );
      },
    }),
    runImmediateReport: builder.mutation({
      queryFn: async ({
        report,
        outputType = "JSON",
        presentationType,
        limitRows,
        skipRows,
        asAt,
      }) =>
        pollUntilReady(
          () =>
            reportsV3BaseQuery({
              path: "reports/run",
              method: "POST",
              body: {
                report,
                runReport: buildRunRequest({
                  outputType,
                  presentationType,
                  limitRows,
                  skipRows,
                  asAt,
                }),
              },
              parseAs: "json",
              operation: "Failed to run immediate report",
            }),
          "run immediate report",
          outputType
        ),
    }),
    getReportConfiguration: builder.query({
      queryFn: async ({ reportId, configurationName }) => {
        if (reportId === DASHBOARD_REPORT_ID) {
          return { data: { metrics: INITIAL_METRICS } };
        }
        try {
          const response = await authenticatedFetch(
            endpoints.reportsV3(
              `reports/${reportId}/configuration/${configurationName}`
            )
          );
          if (response.status === 404) {
            return {
              data: {},
            };
          }
          if (!response.ok) {
            throw new Error(
              `HTTP ${response.status}: Failed to fetch configuration`
            );
          }
          const data = await response.json();
          return {
            data,
          };
        } catch (error) {
          logger.info(
            `${LOG_PREFIX} Failed to fetch configuration ${configurationName} for report ${reportId}:`,
            error
          );
          return {
            data: {},
          };
        }
      },
      providesTags: (result, error, { reportId, configurationName }) => [
        {
          type: "SavedReports",
          id: `${reportId}-config-${configurationName}`,
        },
      ],
    }),
    storeReportConfiguration: builder.mutation({
      queryFn: async ({ reportId, configurationName, configuration }) => {
        if (reportId === DASHBOARD_REPORT_ID) {
          return { data: {} };
        }
        return reportsV3BaseQuery({
          path: `reports/${reportId}/configuration/${configurationName}`,
          method: "POST",
          body: configuration,
          expectOk: true,
          operation: `Failed to store configuration ${configurationName} for report ${reportId}`,
          logLevel: "warn",
        });
      },
      invalidatesTags: (result, error, { reportId, configurationName }) => [
        {
          type: "SavedReports",
          id: `${reportId}-config-${configurationName}`,
        },
      ],
    }),
    getAvailableAggregators: builder.query({
      query: () => ({
        path: "functions",
        parseAs: "json",
        operation: "Failed to fetch available aggregators",
      }),
    }),
    getColumnsForReport: builder.query({
      queryFn: async (reportId) => {
        if (!reportId) return ID_ERROR;
        let query = null;
        if (reportId === DASHBOARD_REPORT_ID) {
          try {
            const { report: fullReport } = await buildDashboardReport();
            const {
              allUsers,
              canWrite,
              configuration,
              readOnly,
              ...report
            } = fullReport;
            query = {
              path: "reports/run",
              method: "POST",
              body: { report, runReport: buildRunRequest({ limitRows: 0 }) },
              parseAs: "json",
              operation: `Failed to execute report ${reportId}`,
            };
          } catch (error) {
            return createApiError("Failed to build dashboard report", error);
          }
        }
        if (!query) {
          query = {
            path: `reports/${reportId}/run`,
            method: "POST",
            body: { type: "JSON", presentationType: "INLINE", limitRows: 0 },
            parseAs: "json",
            operation: `Failed to fetch columns for report ${reportId}`,
          };
        }
        const result = await pollUntilReady(
          () => reportsV3BaseQuery(query),
          `fetch columns for report ${reportId}`
        );
        if (result.error) return { error: result.error };
        return {
          data: columnDescriptorsToColumnDefinitions(
            result.data?.[0]?.columns ?? []
          ),
        };
      },
    }),
    getColumnsForImmediateReport: builder.query({
      queryFn: async (report) => {
        const result = await pollUntilReady(
          () =>
            reportsV3BaseQuery({
              path: "reports/run",
              method: "POST",
              body: { report, runReport: buildRunRequest({ limitRows: 0 }) },
              parseAs: "json",
              operation: "Failed to fetch columns for immediate report",
            }),
          "fetch columns for immediate report"
        );
        if (result.error) return { error: result.error };
        return {
          data: columnDescriptorsToColumnDefinitions(
            result.data?.[0]?.columns ?? []
          ),
        };
      },
    }),
  }),
});
export const {
  useGetTablesQuery,
  useGetColumnsForTablesQuery,
  useGetEnumValuesForTablesQuery,
  useGetSavedReportsQuery,
  useGetReportQuery,
  useLazyGetReportQuery,
  useGetReportMetadataQuery,
  useUpdateReportMetadataMutation,
  useCreateReportMutation,
  useUpdateReportMutation,
  useDeleteReportMutation,
  useExecuteReportMutation,
  useRunImmediateReportMutation,
  useLazyGetColumnsForTablesQuery,
  useGetColumnsForReportQuery,
  useLazyGetColumnsForReportQuery,
  useGetColumnsForImmediateReportQuery,
  useLazyGetColumnsForImmediateReportQuery,
  useGetReportConfigurationQuery,
  useLazyGetReportConfigurationQuery,
  useStoreReportConfigurationMutation,
  useGetAvailableAggregatorsQuery,
} = reportsV3Api;
